import myAxios from './axiositeachWrapper';
import Config from '../_config/config';

export interface UsageBucket {
  calls: number;
  inputTokens: number;
  outputTokens: number;
  estimatedCost: number;
}

export interface ServiceUsage {
  allTime: UsageBucket;
  thisMonth: UsageBucket;
  lastMonth: UsageBucket;
}

export interface RateLimits {
  requestsLimit: number | null;
  requestsRemaining: number | null;
  tokensLimit: number | null;
  tokensRemaining: number | null;
  inputTokensLimit: number | null;
  inputTokensRemaining: number | null;
  outputTokensLimit: number | null;
  outputTokensRemaining: number | null;
}

export interface AnthropicStatus {
  configured: boolean;
  maskedKey: string | null;
  healthy: boolean;
  healthError: string | null;
  model: string | null;
  envModel: string | null;
  tenantModel: string | null;
  usage: ServiceUsage;
  rateLimits: RateLimits | null;
  billingUrl: string;
}

export interface SupportedModel {
  id: string;
  label: string;
}

export interface OpenAIStatus {
  configured: boolean;
  maskedKey: string | null;
  healthy: boolean;
  healthError: string | null;
  model: string | null;
  usage: ServiceUsage;
  billingUrl: string;
}

export interface PineconeStatus {
  configured: boolean;
  maskedKey: string | null;
  healthy: boolean;
  healthError: string | null;
  indexName: string | null;
  indexStats: {
    totalRecordCount: number;
    dimension: number;
    indexFullness: number;
    namespaces: Record<string, { recordCount: number }>;
  } | null;
  billingUrl: string;
}

export interface MonthlyBudget {
  tokensUsed: number;
  requestsMade: number;
  estimatedCostUsd: number;
  maxTokens: number | null;
  maxRequests: number | null;
  maxEstimatedCostUsd: number | null;
}

export interface AiSubscriptionsData {
  anthropic: AnthropicStatus;
  openai: OpenAIStatus;
  pinecone: PineconeStatus;
  monthlyBudget?: MonthlyBudget;
  usageError: string | null;
  lastChecked: string;
}

class AiSubscriptionsService {
  private baseUrl: string;

  constructor() {
    this.baseUrl = `${Config.nodeSiteUrl}v1/ai-subscriptions`;
  }

  async getStatus(): Promise<{ success: boolean; data?: AiSubscriptionsData; error?: string }> {
    try {
      const response = await myAxios.get(`${this.baseUrl}/status`);
      return response.data;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.message || 'Failed to fetch AI subscriptions status',
      };
    }
  }

  async getSupportedModels(): Promise<{ success: boolean; data?: { models: SupportedModel[] }; error?: string }> {
    try {
      const response = await myAxios.get(`${this.baseUrl}/supported-models`);
      return response.data;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.message || 'Failed to fetch supported models',
      };
    }
  }

  async updateDefaultModel(model: string | null): Promise<{
    success: boolean;
    data?: { tenantModel: string | null; effectiveModel: string };
    message?: string;
    error?: string;
  }> {
    try {
      const response = await myAxios.put(`${this.baseUrl}/default-model`, { model });
      return response.data as any;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.message || 'Failed to update default model',
      };
    }
  }
}

export default new AiSubscriptionsService();
